import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Radio, Select, Table, Tooltip } from 'antd';
import { AudioMutedOutlined, AudioOutlined, SoundFilled, SoundOutlined } from '@ant-design/icons';
import { setConnectionAudioSourceDevice, setEventAudioMuted, setEventAudioSource } from 'api/audio-sources';
import { useEventAdminState } from 'components/EventStateProvider';
import { isTestingMode } from 'components/TestingMode';
import { useI18n } from 'components/I18nProvider';
import { useLiveInputAudioMonitor } from 'lib/use-live-input-audio-monitor';
import AudioPeakIndicator from './AudioPeakIndicator';
import styles from './style.module.scss';
const AudioSourcesPanel = ({ eventId, micEnabled }) => {
    const { intl } = useI18n();
    const { eventSnapshot, eventLiveTelemetry, wsClient, connectionId, transportOnline, } = useEventAdminState();
    const connections = (eventSnapshot?.connections ?? [])
        .filter((connection) => connection.audioSources.length > 0);
    const selectedSourceId = eventSnapshot?.selectedAudioSourceConnectionId ?? null;
    const audioMuted = eventSnapshot?.audioMuted ?? false;
    const { listening, canListen, toggleListening } = useLiveInputAudioMonitor({
        wsClient,
        transportOnline,
        selectedAudioSourceConnectionId: selectedSourceId,
    });
    const sourceColumnTitle = isTestingMode()
        ? intl.formatMessage({ id: 'testRecording' })
        : intl.formatMessage({ id: 'microphone' });
    const columns = useMemo(() => [
        {
            title: intl.formatMessage({ id: 'audioSource' }),
            key: 'source',
            width: 112,
            ellipsis: true,
            className: styles.sourceColumn,
            render: (_, connection) => {
                const sourceLabel = `#${connection.connectionNumber}`;
                return (_jsxs("div", { className: styles.sourceCell, children: [_jsx(Radio, { value: connection.id, disabled: !transportOnline, "aria-label": intl.formatMessage({ id: 'selectAudioSource' }) }), _jsxs("span", { className: styles.sourceName, title: sourceLabel, children: ['🎤 ', sourceLabel] })] }));
            },
        },
        {
            title: sourceColumnTitle,
            key: 'audioSource',
            ellipsis: true,
            render: (_, connection) => {
                const isLocalConnection = connection.id === connectionId;
                return (_jsx(Select, { "test-id": "admin-audio-device-select", className: styles.sourceSelect, placeholder: intl.formatMessage({ id: 'pleaseSelect' }), value: connection.selectedAudioSourceDeviceId ?? undefined, options: connection.audioSources.map((source) => ({
                        value: source.deviceId,
                        label: source.label,
                    })), onChange: (deviceId) => {
                        setConnectionAudioSourceDevice(eventId, connection.id, deviceId);
                    }, allowClear: true, disabled: !transportOnline || (isLocalConnection && !micEnabled), onClear: () => {
                        setConnectionAudioSourceDevice(eventId, connection.id, null);
                    } }));
            },
        },
    ], [connectionId, eventId, intl, micEnabled, sourceColumnTitle, transportOnline]);
    return (_jsxs("div", { className: styles.panel, children: [_jsx(Radio.Group, { className: styles.sourceRadioGroup, value: selectedSourceId, onChange: (event) => {
                    setEventAudioSource(eventId, event.target.value);
                }, children: _jsx(Table, { className: styles.table, rowKey: "id", dataSource: connections, columns: columns, pagination: false, size: "small", tableLayout: "fixed", rowClassName: (record) => (record.id === selectedSourceId ? styles.selectedRow : ''), locale: {
                        emptyText: (_jsx("span", { className: styles.emptyTableMessage, children: intl.formatMessage({ id: 'noActiveAudioSources' }) })),
                    } }) }), _jsxs("div", { className: styles.peakRow, children: [_jsx(Tooltip, { title: intl.formatMessage({ id: 'muteMicrophone' }), children: _jsx("button", { type: "button", "test-id": "admin-audio-mute-button", className: audioMuted ? styles.muteButtonActive : styles.muteButton, onClick: () => {
                                setEventAudioMuted(eventId, !audioMuted);
                            }, disabled: !transportOnline, "aria-pressed": audioMuted, "aria-label": intl.formatMessage({ id: 'muteMicrophone' }), children: audioMuted ? _jsx(AudioMutedOutlined, {}) : _jsx(AudioOutlined, {}) }) }), _jsx(AudioPeakIndicator, { peakLevel: eventLiveTelemetry?.audioPeakLevel ?? 0 }), _jsx(Tooltip, { title: intl.formatMessage({
                            id: listening ? 'stopListeningToLiveAudio' : 'listenToLiveAudio',
                        }), children: _jsx("span", { className: styles.listenButtonWrap, children: _jsx("button", { type: "button", "test-id": "admin-audio-listen-button", className: listening ? styles.listenActive : styles.listenButton, onClick: toggleListening, disabled: !canListen, "aria-pressed": listening, "aria-label": intl.formatMessage({
                                    id: listening ? 'stopListeningToLiveAudio' : 'listenToLiveAudio',
                                }), children: listening ? _jsx(SoundFilled, {}) : _jsx(SoundOutlined, {}) }) }) })] })] }));
};
export default AudioSourcesPanel;
